import {
    Extension,
    pointerUtils,
    CreateExtensionArgument,
    type CreateExtArgs,
    type ExtensionAPI,
    type DalValue,
    type ExtensionPublicAPI
} from '@wix/document-manager-core'
import type {FragmentPointer, Pointer} from '@wix/document-services-types'
import {RESOURCE_TYPES} from '../../constants/constants'
import type {FragmentsExtensionAPI} from '../fragments/fragments'

const {getFragmentPointer} = pointerUtils

interface EditorFeaturesAPI {
    editorFeatures: {
        instances: {
            list(): Promise<string[]>
            create(name?: string): void
        }
        data: {
            getPointer(fragmentInstanceId: string, namespace: string, itemId: string): Pointer | FragmentPointer
            listPointers(namespace: string): (Pointer | FragmentPointer)[]
            get(pointer: Pointer | FragmentPointer): Promise<DalValue>
            remove(pointer: Pointer | FragmentPointer): Promise<void>
            update(pointer: Pointer | FragmentPointer, value: DalValue): Promise<void>
        }
    }
}

export interface EditorFeaturesPublicAPI extends EditorFeaturesAPI, ExtensionPublicAPI {}
export interface EditorFeatureExtensionAPI extends EditorFeaturesAPI, ExtensionAPI {}

const isValidEditorSettingsPointer = (pointer: Pointer) =>
    pointer.fragmentType === RESOURCE_TYPES.EDITOR_FEATURES && pointer.fragmentInstance && pointer.type && pointer.id

const validateEditorSettingsPointer = (pointer: Pointer) => {
    if (!isValidEditorSettingsPointer(pointer)) {
        throw new Error(
            `The given pointer ${JSON.stringify(pointer)} is not a valid ${RESOURCE_TYPES.EDITOR_FEATURES} pointer`
        )
    }
}
const createExtension = ({experimentInstance}: CreateExtensionArgument): Extension => {
    const createExtensionAPI = (createExtArgs: CreateExtArgs): EditorFeatureExtensionAPI => {
        const {dal, extensionAPI} = createExtArgs
        const {fragments, fragmentIndex} = extensionAPI as FragmentsExtensionAPI

        const throwIfExperimentClosed = () => {
            if (!experimentInstance.isOpen('dm_fragmentsEditorFeatures')) {
                throw new Error(`The experiemnt dm_fragmentsEditorFeatures must be openned to use this api`)
            }
        }

        return {
            editorFeatures: {
                instances: {
                    async list() {
                        throwIfExperimentClosed()
                        return fragmentIndex.list(RESOURCE_TYPES.EDITOR_FEATURES).map(({id}) => id)
                    },
                    create(name?: string) {
                        throwIfExperimentClosed()
                        if (!name) {
                            throw new Error('Instance name is required')
                        }
                        // Use fragments API to create fragment index entry
                        fragments.addFragmentIndex({
                            type: RESOURCE_TYPES.EDITOR_FEATURES,
                            name
                        })
                    }
                },
                data: {
                    getPointer(fragmentInstance: string, type: string, id: string) {
                        throwIfExperimentClosed()
                        return getFragmentPointer({
                            fragmentType: RESOURCE_TYPES.EDITOR_FEATURES,
                            fragmentInstance,
                            type,
                            id
                        })
                    },
                    listPointers(namespace: string) {
                        throwIfExperimentClosed()
                        return dal.getItemsInNamespace(RESOURCE_TYPES.EDITOR_FEATURES, namespace)
                    },
                    async get(pointer: FragmentPointer) {
                        throwIfExperimentClosed()
                        validateEditorSettingsPointer(pointer)
                        return dal.get(pointer)
                    },
                    async remove(pointer: FragmentPointer) {
                        throwIfExperimentClosed()
                        validateEditorSettingsPointer(pointer)
                        dal.remove(pointer)
                    },
                    async update(pointer: FragmentPointer, value: DalValue) {
                        throwIfExperimentClosed()
                        validateEditorSettingsPointer(pointer)
                        value.metaData = {fragmentInstance: pointer.fragmentInstance}
                        dal.set(pointer, value)
                    }
                }
            }
        }
    }

    const createPublicAPI = (createExtArgs: CreateExtArgs): EditorFeaturesPublicAPI => {
        const {editorFeatures} = createExtArgs.extensionAPI as EditorFeatureExtensionAPI
        return {
            editorFeatures: {
                instances: {
                    list: () => editorFeatures.instances.list(),
                    create: (name?: string) => editorFeatures.instances.create(name)
                },
                data: {
                    getPointer: (fragmentInstance: string, type: string, id: string) =>
                        editorFeatures.data.getPointer(fragmentInstance, type, id),
                    listPointers: (namespace: string) => editorFeatures.data.listPointers(namespace),
                    get: (pointer: FragmentPointer) => editorFeatures.data.get(pointer),
                    remove: (pointer: FragmentPointer) => editorFeatures.data.remove(pointer),
                    update: (pointer: FragmentPointer, value: DalValue) => editorFeatures.data.update(pointer, value)
                }
            }
        }
    }

    return {
        name: 'editorFeatures',
        createExtensionAPI,
        createPublicAPI
    }
}

export {createExtension}
